import { useEffect, useMemo, useRef } from 'react';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import type { TextElement } from '../../shared/document';
import { createTextTexture } from './textTexture';

type Props = {
  element: TextElement;
  family: string;
  draggable: boolean;
  onNode: (node: Konva.Node | null) => void;
  onClick: (event: KonvaEventObject<MouseEvent>) => void;
  onDoubleClick: (event: KonvaEventObject<MouseEvent>) => void;
  onDragStart: (event: KonvaEventObject<DragEvent>) => void;
  onDragMove: (event: KonvaEventObject<DragEvent>) => void;
  onDragEnd: (event: KonvaEventObject<DragEvent>) => void;
};

export function CurvedTextArtwork({ element, family, draggable, onNode, onClick, onDoubleClick, onDragStart, onDragMove, onDragEnd }: Props): React.JSX.Element {
  const texture = useMemo(() => createTextTexture(element, family), [element, family]);
  const imageRef = useRef<Konva.Image | null>(null);
  const hasShadow = element.shadowBlur > 0 || element.shadowOffsetX !== 0 || element.shadowOffsetY !== 0;
  useEffect(() => {
    const node = imageRef.current;
    if (!node) return;
    node.clearCache();
    // The text texture is mostly transparent. Cache its alpha as the hit
    // region so the large box needed for a curve does not block layers below.
    node.cache({ pixelRatio: 2 });
    node.drawHitFromCache(24);
    node.getLayer()?.batchDraw();
  }, [texture, hasShadow, element.shadowBlur, element.shadowColor, element.shadowOffsetX, element.shadowOffsetY]);
  const { cx, cy, width, height, rotation } = element.transform;
  return (
    <KonvaImage
      ref={(node) => { imageRef.current = node; onNode(node); }}
      x={cx}
      y={cy}
      width={width}
      height={height}
      offsetX={width / 2}
      offsetY={height / 2}
      rotation={rotation}
      opacity={element.opacity}
      image={texture}
      shadowEnabled={hasShadow}
      shadowColor={element.shadowColor}
      shadowBlur={element.shadowBlur}
      shadowOffsetX={element.shadowOffsetX}
      shadowOffsetY={element.shadowOffsetY}
      draggable={draggable}
      onClick={onClick}
      onTap={(event) => onClick(event as KonvaEventObject<MouseEvent>)}
      onDblClick={onDoubleClick}
      onDblTap={(event) => onDoubleClick(event as KonvaEventObject<MouseEvent>)}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
    />
  );
}
